import { CanActivateFn, Router, UrlTree, RouterStateSnapshot } from '@angular/router';
import { inject, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { map, catchError } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import { SharedStorageService } from './shared/services/shared-storage.service';
import { ApiResponse } from './shared/interfaces/api-response.model';
import { API_ENDPOINTS } from './shared/constants/api.constants';
import { SKIP_AUTH } from './shared/tokens/http-context.tokens';

export const authguardGuard: CanActivateFn = (route, state): Observable<boolean | UrlTree> | boolean | UrlTree => {
  const router = inject(Router);
  const sharedStorageService = inject(SharedStorageService);
  const http = inject(HttpClient);
  const platformId = inject(PLATFORM_ID);
  const isBrowser = isPlatformBrowser(platformId);

  const currentPath = state.url;

  // 1. 呼叫維護狀態 API (路徑請根據 API 規格書定義修改)
  // 注意：如果是 SSR 環境，建議直接執行邏輯或預設維護關閉
  if (!isBrowser) {
    return runAuthLogic(router, sharedStorageService, state, isBrowser);
  }

  return http.get<ApiResponse<any>>(API_ENDPOINTS.MAINTENANCE_STATUS, {
    // 跳過 AuthInterceptor，減少 Headers 數量，降低 CORS 失敗機率
    context: new HttpContext().set(SKIP_AUTH, true)
  }).pipe(
    map(res => {
      if (res.success && res.data) {
        const m = res.data;
        // 封裝維護時間參數
        const queryParams = {
          month: m.maintenanceMonth,
          day: m.maintenanceDay,
          hour: m.maintenanceHour,
          minute: m.maintenanceMinute
        };

        // A. 全系統維護 (systemMaintenance)
        if (m.systemMaintenance) return router.createUrlTree(['/maintain'], { queryParams });

        // B. 預約流程維護 (bookingMaintenance)
        if ((currentPath.includes('/reserve') && m.bookingMaintenance) ||
          (currentPath.includes('/idlogin') && m.bookingMaintenance) ||
          (currentPath.includes('/companylogin') && m.bookingMaintenance)
        ) return router.createUrlTree(['/maintain'], { queryParams });


        // C. 會員中心 - 本次預約區塊維護 (currentAppointmentMaintenance)
        if (currentPath.includes('/member/mybooking') && m.currentAppointmentMaintenance) {
          return router.createUrlTree(['/maintain'], { queryParams });
        }

        // D. 會員中心 - 健康檔案、報告、修改資料維護 (healthRecordMaintenance)
        const isHealthPath = ['myprofile', 'report', 'editprofile'].some(p => currentPath.includes(p));
        if (isHealthPath && m.healthRecordMaintenance) return router.createUrlTree(['/maintain'], { queryParams });
      }

      // 如果沒在維護，執行原本的權限校驗邏輯
      return runAuthLogic(router, sharedStorageService, state, isBrowser);
    }),
    catchError(() => of(runAuthLogic(router, sharedStorageService, state, isBrowser)))
  );
};

/**
 * 封裝原本的權限與步驟檢查邏輯
 */
function runAuthLogic(router: Router, sharedStorageService: SharedStorageService, state: RouterStateSnapshot, isBrowser: boolean): boolean {
  const currentPath = state.url;

  // 登入頁面只需要維護檢查，不需要權限驗證，直接放行
  if (currentPath.startsWith('/healthcheck/idlogin') || currentPath.startsWith('/healthcheck/companylogin')) {
    return true;
  }
  const reserveType = sharedStorageService.get('reserveType');

  // 進入會員中心時，設定 myHealthProfile 狀態，供登入後判斷是否從會員中心導回
  if (currentPath.startsWith('/healthcheck/member/myprofile')) {
    sharedStorageService.set('myHealthProfile', true);
  }
  // 檢查是否為會員中心路徑
  if (currentPath.startsWith('/healthcheck/member')) {
    // 檢查是否已登入
    const loginStatus = sharedStorageService.get('loginStatus');
    if (loginStatus !== 'memberLogin') {
      router.navigate(['/healthcheck/login']);
      return false;
    }

    // 檢查是否為頁面刷新（允許）或直接網址訪問（阻止）
    let isPageRefresh = false;
    if (isBrowser) {
      const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      isPageRefresh = navigationEntries.length > 0 && navigationEntries[0].type === 'reload';
    }
    const navigation = router.getCurrentNavigation();

    // 檢查是否有 query parameters（例如從外部導回會帶參數）
    const hasQueryParams = state.url.includes('?');

    const isDirectUrl = !navigation ||
      (navigation.trigger === 'imperative' && !navigation.previousNavigation);

    // 如果是直接網址訪問，但有 query parameters 或是頁面刷新，則允許通過
    if (isDirectUrl && !isPageRefresh && !hasQueryParams) {
      console.log('直接網址訪問會員中心，導回首頁');
      router.navigate(['/healthcheck']);
      return false;
    }

    return true;
  }

  // 檢查是否為特殊活動的預約流程
  if (currentPath.startsWith('/healthcheck/healthplan/reserve')) {
    if (!reserveType) {
      router.navigate(['/healthcheck/healthplan']);
      return false;
    }

    // 檢查是否為頁面刷新或直接網址訪問
    let isPageRefresh = false;
    if (isBrowser) {
      const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      isPageRefresh = navigationEntries.length > 0 && navigationEntries[0].type === 'reload';
    }
    const navigation = router.getCurrentNavigation();
    const isDirectUrl = !navigation ||
      (navigation.trigger === 'imperative' && !navigation.previousNavigation);

    if (isDirectUrl && !isPageRefresh) {
      router.navigate(['/healthcheck/healthplan']);
      return false;
    }

    // 檢查預約流程步驟順序
    const currentStep = sharedStorageService.get('reserveStep') || 1;

    const stepPathMap: { [key: number]: string[] } = {
      2: [
        '/healthcheck/healthplan/reserve/personalhistory',
        '/healthcheck/healthplan/reserve/familyhistory',
        '/healthcheck/healthplan/reserve/symptoms'
      ],
      2.5: ['/healthcheck/healthplan/reserve/loading'],
      3: ['/healthcheck/healthplan/reserve/personalplan'],
    };

    const allowedPaths = stepPathMap[currentStep] || [];
    const isValidStep = allowedPaths.some(path => currentPath.startsWith(path));

    if (!isValidStep) {
      router.navigate(['/healthcheck/healthplan']);
      return false;
    }

    return true;
  }

  // 檢查是否為特殊活動頁面(但不是預約流程)
  if (currentPath.startsWith('/healthcheck/healthplan')) {
    return true;
  }

  // 一般預約流程
  if (reserveType) {
    // 檢查是否為頁面刷新 或 直接網址訪問
    let isPageRefresh = false;
    if (isBrowser) {
      const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      isPageRefresh = navigationEntries.length > 0 && navigationEntries[0].type === 'reload';
    }
    const navigation = router.getCurrentNavigation();
    const isDirectUrl = !navigation ||
      (navigation.trigger === 'imperative' && !navigation.previousNavigation);

    if (isDirectUrl && !isPageRefresh) {
      router.navigate(['/healthcheck']);
      return false;
    }

    // 檢查預約流程步驟順序
    const currentStep = sharedStorageService.get('reserveStep') || 1;

    // 定義步驟與路徑的對應關係
    const stepPathMap: { [key: number]: string[] } = {
      1: ['/healthcheck/reserve/location'],
      2: ['/healthcheck/reserve/personalinfo', '/healthcheck/reserve/personalhistory', '/healthcheck/reserve/familyhistory', '/healthcheck/reserve/symptoms'],
      2.5: ['/healthcheck/reserve/loading'],
      3: ['/healthcheck/reserve/personalplan', '/healthcheck/reserve/companyplan', '/healthcheck/reserve/plancompare'],
      4: ['/healthcheck/reserve/additem'],
      5: ['/healthcheck/reserve/overview', '/healthcheck/reserve/evaluation', '/healthcheck/reserve/reminder'],
      6: ['/healthcheck/reserve/bookinfo'],
      7: ['/healthcheck/reserve/timeselect'],
      8: ['/healthcheck/reserve/review'],
      9: ['/healthcheck/reserve/complete']
    };

    // 檢查當前路徑是否符合當前步驟
    const allowedPaths = stepPathMap[currentStep] || [];
    const isValidStep = allowedPaths.some(path => currentPath.startsWith(path));

    if (!isValidStep) {
      //預防直接用網址跳轉
      router.navigate(['/healthcheck']);
      sharedStorageService.clear();
      return false;
    }

    return true;
  } else {
    // 沒有預約類型，表示是直接輸入網址，導回首頁
    router.navigate(['/healthcheck']);
    sharedStorageService.clear();
    return false;
  }
}
